import React from 'react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import axios from 'axios';
import { User } from '../types';
import { API_GATEWAY_URL, GOOGLE_TOKEN_KEY, DEFAULT_HEADERS, getCurrentLanguage } from '../constants';

interface SubscriptionSectionProps {
  user: User | null;
  setUser: (user: User | null) => void;
  subscribed: boolean;
  setSubscribed: (subscribed: boolean) => void;
  isEmailConfirmed: boolean;
  setIsEmailConfirmed: (confirmed: boolean) => void;
  subscribing: boolean;
  setSubscribing: (subscribing: boolean) => void;
  checkingSubscription: boolean;
  error: string | null;
  setError: (error: string | null) => void;
}

const SubscriptionSection: React.FC<SubscriptionSectionProps> = ({
  user,
  setUser,
  subscribed,
  setSubscribed,
  isEmailConfirmed,
  setIsEmailConfirmed,
  subscribing,
  setSubscribing,
  checkingSubscription,
  error,
  setError
}) => {
  const handleUnsubscribe = async (): Promise<void> => {
    if (!user) return;
    
    try {
      await axios.delete(`${API_GATEWAY_URL}/subscriptions/${user.Email}`, {
        headers: DEFAULT_HEADERS
      });
      setSubscribed(false);
      localStorage.removeItem(GOOGLE_TOKEN_KEY);
      setUser(null);
    } catch (err) {
      setError('Failed to unsubscribe');
    }
  };

  return (
    <div className="subscription-section">
      {!user ? (
        !subscribing ? (
          <div className="login-section">
            <p>Sign in with Google to receive daily word notifications:</p>
            <GoogleLogin
              onSuccess={(credentialResponse: CredentialResponse) => {
                const credential = credentialResponse.credential;
                if (!credential) {
                  setError('Invalid credential response');
                  return;
                }
                
                localStorage.setItem(GOOGLE_TOKEN_KEY, credential);

                setSubscribing(true);
                axios.post(`${API_GATEWAY_URL}/subscriptions`, { 
                  credential,
                  language: getCurrentLanguage()
                }, {
                  headers: DEFAULT_HEADERS
                })
                .then((response) => {
                  const payload = JSON.parse(atob(credential.split('.')[1]));
                  setUser({
                    Name: payload.name,
                    Email: payload.email,
                    GoogleId: payload.sub,
                  });
                  setSubscribed(true);
                  setIsEmailConfirmed(true); // Force email confirmed to be true
                  setError(null);
                })
                .catch(err => {
                  setError('Failed to subscribe');
                  setUser(null);
                  localStorage.removeItem(GOOGLE_TOKEN_KEY);
                  console.error(err);
                })
                .finally(() => {
                  setSubscribing(false);
                });
              }}
              onError={() => {
                setError('Login Failed');
              }}
              useOneTap
            />
          </div>
        ) : (
          <p>Subscribing...</p>
        )
      ) : (
        <div className="user-section">
          <p>Logged in as {user.Name}</p>
          {checkingSubscription ? (
            <p>Retrieving subscription status...</p>
          ) : subscribed ? (
            <div>
              <p className="subscribed-message">
                You are subscribed to daily word notifications!
              </p>
              {/* Email confirmation check disabled
                {!isEmailConfirmed && (
                  <p className="email-confirmation-message">
                    Please confirm your email address to receive notifications.
                    Check your inbox for a confirmation email.
                  </p>
                )}
              */}
              <button className="unsubscribe-button" onClick={handleUnsubscribe}>
                Unsubscribe
              </button>
            </div>
          ) : (
            <div>
              <p>You are not subscribed to notifications</p>
              {!subscribing && 
              <button 
                className="subscribe-button" 
                onClick={() => {
                  setSubscribing(true);
                  axios.post(`${API_GATEWAY_URL}/subscriptions`, { 
                    credential: localStorage.getItem(GOOGLE_TOKEN_KEY),
                    language: getCurrentLanguage()
                  }, {
                    headers: DEFAULT_HEADERS
                  })
                  .then((response) => {
                    setSubscribed(true);
                    setIsEmailConfirmed(true); // Force email confirmed to be true
                    setError(null);
                  })
                  .catch(err => {
                    setError('Failed to subscribe');
                    setUser(null);
                    localStorage.removeItem(GOOGLE_TOKEN_KEY);
                    console.error(err);
                  })
                  .finally(() => {
                    setSubscribing(false);
                  });
                }}
              >
                Subscribe
              </button>
              }
              {subscribing && <p>Subscribing...</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionSection;